import { waypointMarkerFactory } from 'common/utils/waypointMarker';
import getGUID from 'common/utils/getGUID';
import nearestPointOnLine from '@turf/nearest-point-on-line';
import { point as createTurfPoint, lineString } from '@turf/helpers';
import { Position } from 'geojson';

import { intl } from 'i18n';

import { DEFAULT_SERVICE_POINT_DISPLAY_RADIUS } from 'common/constants/servicePointDisplayRadius';

import { Map } from "common/utils/Map";
import { GeoCoords } from 'common/types/map/geoCoords';
import { WaypointMarker } from 'common/utils/waypointMarker/types/waypointMarker';
import {
  Params,
  Route,
  Fn as AddListenerClickFn
} from '../types';

import InfoWindow from './InfoWindow';
export class GoogleRoute implements Route {
  private _map: Map;
  private _waypointMarkers: WaypointMarker[] = [];
  private _id: string;
  private _infoWindow?: InfoWindow;
  private _directionResult?: Params['directionResult'];
  private _directionsRenderer?: google.maps.DirectionsRenderer;
  private _polylineOptions = {
    strokeColor: '#008DC9',
    strokeWeight: 5,
    zIndex: 0,
    clickable: false
  };
  public sections: Params['sections'];
  public routePoints: Params['routePoints'];
  public time: Params['time'];
  public startAddress: Params['startAddress'];
  public endAddress: Params['endAddress'];
  public distance: Params['distance'];
  public bounds: Params['bounds'];
  public nativeBounds: Params['nativeBounds'];
  public legs: Params['legs'];
  public overviewPath: Params['overviewPath'];
  public turfLinestring: Route['turfLinestring'];

  constructor(map: Map, params: Params) {
    const {
      time,
      startAddress,
      endAddress,
      distance,
      bounds,
      nativeBounds,
      legs,
      overviewPath,
      directionResult,
      routePoints,
      sections
    } = params;

    this._map = map;
    this._id = getGUID();
    this._directionResult = directionResult;
    this.time = time;
    this.startAddress = startAddress;
    this.endAddress = endAddress;
    this.distance = distance;
    this.bounds = bounds;
    this.nativeBounds = nativeBounds;
    this.legs = legs;
    this.routePoints = routePoints;
    this.overviewPath = overviewPath;
    this.sections = sections;
    this.turfLinestring = lineString<{}>(overviewPath
      .reduce((acc, item) => {
        acc.push([item.lng, item.lat]);
        return acc;
    }, [] as Position[]));
    this._createDirectionRenderer();
  }

  public get id() {
    return this._id;
  }

  _createDirectionRenderer(
    map?: google.maps.Map,
    polylineOptions?: google.maps.PolylineOptions
  ) {
    if (this._directionResult) {
      this._directionsRenderer = new google.maps.DirectionsRenderer({
        map,
        markerOptions: { visible: false },
        polylineOptions: { ...this._polylineOptions, ...polylineOptions },
        preserveViewport: true,
        directions: this._directionResult
      });
    }
  }

  _createWaypointMarkers() {
    if (this.routePoints) {
      const routePoints = [
        this.routePoints.firstPoint,
        ...this.routePoints.waypoints,
        this.routePoints.lastPoint
      ];

      this._waypointMarkers = routePoints.map(({ coordinates }, index) => {
        let name = '';
        
        if (index === 0) {
          name = 'startPoint';
        } else if (index === routePoints.length - 1) {
          name = 'endPoint';
        } else {
          name = `waypoint${index}`;
        }

        return waypointMarkerFactory(
          this._map,
          // @ts-ignore
          { location: { lat: coordinates?.lat, lng: coordinates?.lng }, name }
        )
      });
    }
  }

  _deleteWaypointMarkers() {
    this._waypointMarkers.forEach(waypointMarker => {
      waypointMarker.hide();
    });
    this._waypointMarkers = [];
  }

  _showRoute() {
    if (this._directionsRenderer && this._map) {
      // @ts-ignore
      this._directionsRenderer.setMap(this._map.getNativeMap());
    }
  }

  _deleteRoute() {
    if (this._directionsRenderer) {
      this._directionsRenderer.setMap(null);
    }
  }

  get overviewPolyline(): undefined | string {
    if (this._directionResult) {
      return this._directionResult.routes[0].overview_polyline;
    }
    return undefined;
  }

  addInfoWindow() {
    if (this.overviewPath) {
      this._infoWindow = new InfoWindow(
        this._map,
        {
          overviewPath: this.overviewPath,
          distance: `${this.distance} ${intl.formatMessage({ id: 'km' })}`,
          time: this.time
        }
      );
      this._infoWindow.add();
    }
  }


  deleteInfoWindow() {
    if (this._infoWindow) this._infoWindow.delete();
  }

  selectRoute(isSelected: boolean) {
    this._deleteRoute();

    const polylineOptions = {
      strokeColor: isSelected ? '#4FBC46' : this._polylineOptions.strokeColor,
      zIndex: isSelected ? 5 : 0,
    };

    this._createDirectionRenderer(this._map.getNativeMap(), polylineOptions);
  }

  show() {
    this._showRoute();
    this._createWaypointMarkers();
  }

  delete() {
    this._deleteRoute();
    this._deleteWaypointMarkers();
    this.deleteInfoWindow();
  }

  checkIsLocationOnRoute(point: GeoCoords, tolerance: number = DEFAULT_SERVICE_POINT_DISPLAY_RADIUS): boolean {
    const turfPoint = createTurfPoint([point.longitude, point.latitude]);
    const { properties: { dist } } = nearestPointOnLine(this.turfLinestring, turfPoint, { units: 'kilometers' });
    return dist ? dist <= tolerance : true;
  }

  addListenerClick(fn: AddListenerClickFn) { }
}
